<script>
  import { createEventDispatcher } from 'svelte';
  import { Upload, X, FileSignature, CheckCircle2, XCircle, AlertTriangle } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';
  import { api } from '../stores/api.js';
  import SignatureStatePanel from './SignatureStatePanel.svelte';
  import { SIGNATURE_STATES, signatureStateOf } from './signatureState.js';

  export let proposalId = null;
  export let proposalNumber = '';
  export let type = 'customer'; // 'customer' or 'final'
  export let isCustomerUser = false; // Whether the current user is a customer

  const dispatch = createEventDispatcher();

  let file = null;
  let loading = false;
  let error = null;
  let uploadResult = null;
  let dragOver = false;

  // For customers: show "Upload Signed PDF", for admins: show specific type
  $: title = isCustomerUser
    ? $_('uploadSignedPdf.title')
    : (type === 'customer' ? $_('uploadSignedPdf.titleCustomer') : $_('uploadSignedPdf.titleFinal'));

  $: subtitle = isCustomerUser
    ? $_('uploadSignedPdf.subtitle')
    : (type === 'customer' ? $_('uploadSignedPdf.subtitleCustomer') : $_('uploadSignedPdf.subtitleFinal'));

  $: expectedSignatures = type === 'customer' ? 1 : 2;

  function handleFileSelect(event) {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    dragOver = false;
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  }

  function handleDragOver(event) {
    event.preventDefault();
    dragOver = true;
  }

  function handleDragLeave() {
    dragOver = false;
  }

  // Allowed file types for signed documents
  const ALLOWED_MIMETYPES = ['application/pdf', 'application/pkcs7-mime', 'application/x-pkcs7-mime', 'application/pkcs7-signature', 'application/octet-stream'];
  const ALLOWED_EXTENSIONS = ['.pdf', '.p7m'];

  function isAllowedFile(selectedFile) {
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    return ALLOWED_MIMETYPES.includes(selectedFile.type) || ALLOWED_EXTENSIONS.includes(ext);
  }

  function setFile(selectedFile) {
    if (!isAllowedFile(selectedFile)) {
      error = $_('uploadSignedPdf.pdfOnly');
      file = null;
      return;
    }
    file = selectedFile;
    error = null;
    uploadResult = null;
  }

  function clearFile() {
    file = null;
    error = null;
    uploadResult = null;
  }

  async function handleUpload() {
    if (!file) return;

    loading = true;
    error = null;
    uploadResult = null;

    try {
      console.log(`[UploadSignedPdfModal] Uploading ${type}-signed PDF for proposal ${proposalId} (isCustomer: ${isCustomerUser})`);

      const formData = new FormData();
      formData.append('pdf', file);

      // Use customer portal API for customers, internal API for admins
      const endpoint = isCustomerUser
        ? `/customer-portal/proposals/${proposalId}/upload-signed`
        : `/proposals/${proposalId}/upload-signed?type=${type}`;

      const response = await api.uploadFile(endpoint, formData);

      console.log(
        `[UploadSignedPdfModal] Upload successful (state: ${response.signatureState || 'unknown'}):`,
        response
      );
      uploadResult = {
        success: true,
        // The document is stored either way; the state decides what the screen
        // is allowed to claim about it, and whether the next act will need an
        // attestation.
        signatureState: response.signatureState || signatureStateOf(response.signatureInfo),
        signatureInfo: response.signatureInfo
      };

      // Notify parent of successful upload
      dispatch('uploaded', {
        type,
        signatureState: uploadResult.signatureState,
        signatureInfo: response.signatureInfo
      });
    } catch (err) {
      console.error('[UploadSignedPdfModal] Upload error:', err);

      // Check if error includes signature info
      if (err.signatureInfo) {
        uploadResult = {
          success: false,
          error: err.message,
          signatureInfo: err.signatureInfo
        };
      } else {
        error = err.message || $_('uploadSignedPdf.uploadError');
      }
    } finally {
      loading = false;
    }
  }

  function handleClose() {
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 z-50 flex items-center justify-center">
  <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={handleClose}></div>

  <!-- Modal -->
  <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-xl w-full mx-4 scale-in">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent rounded-t-2xl">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
          <Upload class="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('uploadSignedPdf.forProposal')} {proposalNumber || proposalId}</p>
        </div>
      </div>
      {#if !loading}
        <button
          on:click={handleClose}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      {/if}
    </div>

    <!-- Content -->
    <div class="px-6 py-4">
      <!-- Info box -->
      <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
        <div class="flex items-start">
          <FileSignature class="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div class="text-sm text-blue-700 dark:text-blue-300">
            <p class="font-medium">{subtitle}</p>
            <p class="mt-1">{$_('uploadSignedPdf.expectedSignatures', { values: { count: expectedSignatures } })}</p>
          </div>
        </div>
      </div>

      <!-- Drop zone -->
      {#if !file && !uploadResult?.success}
        <div
          class="border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-200 {dragOver ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'}"
          on:drop={handleDrop}
          on:dragover={handleDragOver}
          on:dragleave={handleDragLeave}
          role="region"
        >
          <Upload class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
          <p class="text-gray-600 dark:text-gray-300 mb-2">{$_('uploadSignedPdf.dragDrop')}</p>
          <p class="text-gray-500 dark:text-gray-400 text-sm mb-4">{$_('uploadSignedPdf.or')}</p>
          <label class="btn btn-primary cursor-pointer">
            {$_('uploadSignedPdf.browse')}
            <input
              type="file"
              accept=".pdf,.p7m,application/pdf,application/pkcs7-mime"
              class="hidden"
              on:change={handleFileSelect}
            />
          </label>
        </div>
      {/if}

      <!-- Selected file -->
      {#if file && !uploadResult?.success}
        <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between">
          <div class="flex items-center">
            <FileSignature class="h-8 w-8 text-primary-500 mr-3" />
            <div>
              <p class="font-medium text-gray-900 dark:text-white">{file.name}</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
            </div>
          </div>
          {#if !loading}
            <button
              on:click={clearFile}
              class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          {/if}
        </div>
      {/if}

      <!-- Error message -->
      {#if error}
        <div class="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start">
            <XCircle class="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
            <p class="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      {/if}

      <!-- Upload result -->
      {#if uploadResult}
        <div class="mt-4">
          {#if uploadResult.success}
            <!-- The upload succeeded; whether the signature was *verified* is a
                 different question, and the answer is shown rather than assumed. -->
            <div class="p-4 rounded-lg border {uploadResult.signatureState === SIGNATURE_STATES.BOUND
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'}">
              <div class="flex items-start">
                {#if uploadResult.signatureState === SIGNATURE_STATES.BOUND}
                  <CheckCircle2 class="h-5 w-5 text-green-500 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                {:else}
                  <AlertTriangle class="h-5 w-5 text-orange-500 dark:text-orange-400 mt-0.5 mr-2 flex-shrink-0" />
                {/if}
                <div class="space-y-2 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-100">{$_('uploadSignedPdf.uploadSuccess')}</p>
                  {#if uploadResult.signatureInfo?.count}
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                      {$_('uploadSignedPdf.signaturesFound', { values: { count: uploadResult.signatureInfo.count } })}
                    </p>
                  {/if}
                  <SignatureStatePanel info={uploadResult.signatureInfo} showTechnicalDetail={!isCustomerUser} />
                  {#if uploadResult.signatureState === SIGNATURE_STATES.UNBOUND}
                    <p class="text-xs text-orange-700 dark:text-orange-300">{$_('uploadSignedPdf.attestationWillBeRequired')}</p>
                  {/if}
                </div>
              </div>
            </div>
          {:else}
            <div class="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <div class="flex items-start">
                <AlertTriangle class="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p class="text-sm font-medium text-red-700 dark:text-red-300">{uploadResult.error}</p>
                  {#if uploadResult.signatureInfo?.count !== undefined}
                    <p class="text-sm text-red-600 dark:text-red-400 mt-1">
                      {$_('uploadSignedPdf.signaturesFound', { values: { count: uploadResult.signatureInfo.count } })}
                    </p>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 rounded-b-2xl">
      <button
        type="button"
        on:click={handleClose}
        class="btn btn-secondary"
        disabled={loading}
      >
        {uploadResult?.success ? $_('common.close') : $_('common.cancel')}
      </button>
      {#if file && !uploadResult?.success}
        <button
          type="button"
          on:click={handleUpload}
          class="btn btn-primary"
          disabled={loading}
        >
          {#if loading}
            <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {$_('uploadSignedPdf.uploading')}
          {:else}
            {$_('uploadSignedPdf.upload')}
          {/if}
        </button>
      {/if}
    </div>
  </div>
</div>
